import { queryOptions } from '@tanstack/react-query'
import { apiGet, apiPost, type PostValue } from './client'

/**
 * Problems - `application/controllers/Problems.php`, la /app/problems.
 *
 * Adresele sunt cele din portal, litera cu litera: `/problems`,
 * `/problems/candidates` si `/problems/view/{id}`. Legaturi catre ele stau
 * scrise in tabloul ITIL, pe fila Problems a randului desfacut din CMDB, si in
 * semnele de carte ale oamenilor.
 */

export interface Pair {
  value: number
  label: string
}

export interface ProblemRow {
  id: number
  title: string
  /** Ocolirea, chiar in rand: e singurul lucru de aici de care are nevoie cineva noaptea. */
  workaround: string
  status: number
  statusLabel: string
  statusColour: string
  incidents: number
  ciId: number
  /** Gol cu `ciId` peste zero = elementul a fost sters din CMDB. */
  ciName: string
  created: string
}

export interface ProblemsListPage {
  hasTables: boolean
  problems: ProblemRow[]
  total: number
  start: number
  limit: number
  statuses: Pair[]
  colours: Record<string, string>
  companies: { id: number; name: string }[]
  filters: {
    search: string
    status: string
    open_only: boolean
    company_id: number
    ci_id: number
  }
  migration: string
}

export interface ProblemIncident {
  id: number
  subject: string
  created: string
  closed: boolean
  statusName: string
  statusColour: string
}

export interface ProblemRecordPage {
  problem: ProblemRow & {
    description: string
    rootCause: string
    companyId: number
    resolved: string
  }
  incidents: ProblemIncident[]
  statuses: Pair[]
  colours: Record<string, string>
}

export interface PatternRow {
  ciId: number
  ciName: string
  criticality: number | null
  environment: number
  incidents: number
  firstSeen: string
  lastSeen: string
  /** Ritmul: o data la cate zile. `null` cand exista un singur interval. */
  every: number | null
}

export interface PatternsPage {
  candidates: PatternRow[]
  options: { days: number; min: number; types: number[] }
  limit: number
  windows: number[]
  minimums: number[]
  types: { ID: number; type_name: string }[]
  environments: Record<string, string>
}

export interface PatternIncidentsPanel {
  ciId: number
  days: number
  incidents: ProblemIncident[]
}

export interface ProblemWriteResult {
  status: 'OK' | 'ERROR'
  message?: string
  id?: number
  linked?: number
  asked?: number
  url?: string
}

export interface ProblemsListRequest {
  search: string
  status: string
  company: number
  ci: number
  openOnly: boolean
  start: number
}

export function problemsListQuery(request: ProblemsListRequest) {
  const query = new URLSearchParams()
  if (request.search !== '') query.set('search', request.search)
  if (request.status !== '') query.set('status', request.status)
  if (request.company > 0) query.set('company_id', String(request.company))
  if (request.ci > 0) query.set('ci_id', String(request.ci))
  if (request.openOnly) query.set('open_only', '1')
  if (request.start > 0) query.set('start', String(request.start))
  const suffix = query.toString() === '' ? '' : `?${query.toString()}`

  return queryOptions({
    queryKey: ['problems', 'list', request],
    queryFn: () => apiGet<ProblemsListPage>(`/problems${suffix}`),
  })
}

/**
 * Numarul de tipare, cerut DUPA ce pagina e pe ecran - si niciodata inaintea ei.
 *
 * Interogarea din spate aduna tichetele din ultimele 90 de zile, din doua tabele
 * si pe doua drumuri: 200 ms cu indexurile potrivite, 32 de SECUNDE fara ele.
 * Pusa pe drumul listei, ea hotara daca lista de probleme se mai deschide - or
 * lista n-are nicio treaba cu tiparele. Daca pica, butonul ramane fara numar si
 * ecranul merge mai departe: un numar de pe un buton n-are voie sa fie singurul
 * lucru care poate darama o pagina.
 */
export const problemsCandidateCountQuery = queryOptions({
  queryKey: ['problems', 'candidate-count'],
  queryFn: () => apiGet<{ status: string; count?: number }>('/problems/candidate-count'),
})

export function problemsPatternsQuery(days: number, min: number, types: number[]) {
  const query = new URLSearchParams({ days: String(days), min: String(min) })
  types.forEach((type) => query.append('types[]', String(type)))

  return queryOptions({
    queryKey: ['problems', 'patterns', days, min, types],
    queryFn: () => apiGet<PatternsPage>(`/problems/candidates?${query.toString()}`),
  })
}

export function problemPatternIncidentsQuery(ciId: number, days: number, types: number[]) {
  const query = new URLSearchParams({ days: String(days) })
  types.forEach((type) => query.append('types[]', String(type)))

  return queryOptions({
    queryKey: ['problems', 'patterns', ciId, 'incidents', days, types],
    queryFn: () => apiGet<PatternIncidentsPanel>(`/problems/candidates/${ciId}/incidents?${query.toString()}`),
  })
}

export function problemRecordQuery(id: number) {
  return queryOptions({
    queryKey: ['problems', 'record', id],
    queryFn: () => apiGet<ProblemRecordPage>(`/problems/${id}`),
  })
}

export function createProblem(fields: Record<string, PostValue>) {
  return apiPost<ProblemWriteResult>('/problems', fields)
}

export function updateProblem(id: number, fields: Record<string, PostValue>) {
  return apiPost<ProblemWriteResult>(`/problems/${id}`, fields)
}

export function removeProblem(id: number) {
  return apiPost<ProblemWriteResult>(`/problems/${id}/remove`, {})
}

export function linkIncidents(id: number, tickets: string) {
  return apiPost<ProblemWriteResult>(`/problems/${id}/link`, { tickets })
}

export function unlinkIncident(id: number, ticket: number) {
  return apiPost<ProblemWriteResult>(`/problems/${id}/unlink`, { ticket })
}
